/**
 * GET /v1/sections/{section_id}/schedule — a section's forward-only schedule.
 *
 * Member-gated, school-scoped, offering-type-neutral. Returns a discriminated
 * status + a list of `ScheduledItem`s with the CALLER'S OWN RSVP only. Athletics
 * is the only projector wired today; the route and contract stay neutral so a
 * future academic projector reuses them.
 *
 * `:section_id` is the section's public CUID (consistent with the other
 * `/sections/:section_id/...` sub-resources).
 *
 * NOTE on naming: the live-BB person-scoped meetings contract
 * (`api/schedule/meetings.ts`) already owns `GetSectionScheduleRequest*` /
 * `GetSectionScheduleResponse*`. This NEW section-scoped read is a distinct axis,
 * so its contract types are prefixed `SectionScheduleSurface*` to avoid the
 * barrel collision while keeping the public ROUTE neutral.
 *
 * See: docs/_archive/2026-10-17-plans-sweep/plans/2026-06-16-006-feat-section-schedule-read-surface-plan.md (U1)
 */

import { z } from 'zod'
import { ScheduledItemSchema, ScheduleStatusSchema } from '../../schemas/entities/scheduled-item'

/** Path params for `GET /v1/sections/{section_id}/schedule`. */
export const SectionScheduleSurfaceRequestSchema = z.object({
  /** The section's public CUID. */
  section_id: z.string().min(1)
})
export type SectionScheduleSurfaceRequest = z.infer<typeof SectionScheduleSurfaceRequestSchema>

/**
 * Inner schedule payload. `status` is server-derived (KTD4); `items` is the
 * forward-only list (empty for every non-`OK` status).
 */
export const SectionScheduleDataSchema = z.object({
  status: ScheduleStatusSchema,
  items: z.array(ScheduledItemSchema)
})
export type SectionScheduleData = z.infer<typeof SectionScheduleDataSchema>

/**
 * Response envelope. Wraps the schedule payload in `{ data }` for house
 * consistency with the athletics-availability siblings (summary / respond /
 * teams), which all return `{ data: ... }`.
 */
export const SectionScheduleResponseSchema = z.object({
  data: SectionScheduleDataSchema
})
export type SectionScheduleResponse = z.infer<typeof SectionScheduleResponseSchema>
